import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from '../../core/useAsync';
import {
  ApiError,
  getHouseSettings,
  getOwnerLeagues,
  getOwnerMembers,
  getOwnerOverview,
  putHouseSettings,
  type HousePace,
  type HouseScoringTemplate,
  type HouseSettings,
  type OwnerLeague,
  type OwnerMember,
  type OwnerOverview,
} from '../../core/api';
import { useTheme } from '../useTheme';
import { ThemeSwitch } from '../ThemeSwitch';
import { avatarStyle, initials } from '../format';
import s from './OwnerConsole.module.css';

// The PLATFORM-OWNER console (/owner) — the app builder's view, above any league.
// Only reachable when me.user.is_platform_owner (a configured owner email); the
// route guard in App.tsx enforces it, and every endpoint re-checks server-side.
// Two jobs: (1) see the whole app — totals, leagues, members; (2) own the global
// defaults new leagues inherit (pace timings + starter scoring), no redeploy.

type Tab = 'overview' | 'leagues' | 'members' | 'defaults';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'leagues', label: 'Leagues' },
  { key: 'members', label: 'Members' },
  { key: 'defaults', label: 'Global defaults' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// A shared "the backend for this isn't live yet" panel — these endpoints are
// handed to the backend (build/handoff-owner-console.md); until they ship, a 404/
// 403 shows this instead of a scary error. A real signed-in-but-not-owner 403 can't
// happen here (the route is owner-gated), so 403 also means "endpoint not built".
function Pending({ err }: { err: Error }) {
  const notLive = err instanceof ApiError && (err.status === 404 || err.status === 403 || err.status >= 500);
  return (
    <div className={s.pending}>
      {notLive ? (
        <>
          <b>Waiting on the backend</b>
          <p>This data comes from an owner endpoint that isn’t deployed yet. The console is ready — it’ll fill in as soon as the API ships.</p>
        </>
      ) : (
        <>
          <b>Couldn’t load</b>
          <p>{err.message}</p>
        </>
      )}
    </div>
  );
}

function Overview() {
  const q = useAsync<OwnerOverview>(getOwnerOverview, []);
  if (q.loading) return <div className={s.loading}>Loading…</div>;
  if (q.error || !q.data) return <Pending err={q.error ?? new Error('No data')} />;
  const t = q.data.totals;
  const tiles: { label: string; value: number }[] = [
    { label: 'Leagues', value: t.leagues },
    { label: 'Members', value: t.members },
    { label: 'Active rounds', value: t.active_rounds },
  ];
  return (
    <div className={s.tiles}>
      {tiles.map((x) => (
        <div key={x.label} className={s.tile}>
          <span className={s.tval}>{x.value.toLocaleString()}</span>
          <span className={s.tlbl}>{x.label}</span>
        </div>
      ))}
    </div>
  );
}

function Leagues() {
  const q = useAsync<OwnerLeague[]>(getOwnerLeagues, []);
  if (q.loading) return <div className={s.loading}>Loading…</div>;
  if (q.error) return <Pending err={q.error} />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <div className={s.empty}>No leagues yet.</div>;
  return (
    <div className={s.tablewrap}>
      <table className={s.table}>
        <thead>
          <tr><th>League</th><th>Mode</th><th className={s.num}>Members</th><th>Owner</th><th>Created</th><th>Status</th></tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.league_id}>
              <td><b>{l.name}</b></td>
              <td><span className={s.pill}>{l.league_type === 'jukebox' ? 'Jukebox' : 'DJ'}</span></td>
              <td className={s.num}>{l.member_count}</td>
              <td>{l.owner_display_name ?? '—'}</td>
              <td className={s.dim}>{fmtDate(l.created_at)}</td>
              <td>{l.paused ? <span className={s.paused}>Paused</span> : <span className={s.live}>Live</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Members() {
  const q = useAsync<OwnerMember[]>(getOwnerMembers, []);
  if (q.loading) return <div className={s.loading}>Loading…</div>;
  if (q.error) return <Pending err={q.error} />;
  const rows = q.data ?? [];
  if (rows.length === 0) return <div className={s.empty}>No members yet.</div>;
  return (
    <div className={s.tablewrap}>
      <table className={s.table}>
        <thead>
          <tr><th>Member</th><th>Email</th><th className={s.num}>Leagues</th><th>Joined</th></tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.user_id}>
              <td>
                <span className={s.person}>
                  <span className={s.av} style={avatarStyle(m.avatar_url, m.user_id)}>{m.avatar_url ? '' : initials(m.display_name)}</span>
                  <b>{m.display_name}</b>
                </span>
              </td>
              <td className={s.dim}>{m.email ?? '—'}</td>
              <td className={s.num}>{m.league_count}</td>
              <td className={s.dim}>{fmtDate(m.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Global defaults editor -----------------------------------------------

function numField(v: number, set: (n: number) => void, min?: number, max?: number) {
  return (
    <input
      className={s.numin}
      type="number"
      value={Number.isFinite(v) ? v : ''}
      min={min}
      max={max}
      inputMode="numeric"
      onChange={(e) => set(e.target.value === '' ? NaN : Number(e.target.value))}
    />
  );
}

function Defaults() {
  const q = useAsync<HouseSettings>(getHouseSettings, []);
  // Edit against a local working copy; `baseline` is the JSON of the last-saved
  // state, so "dirty" means "differs from what's on the server" — a save clears it.
  const [form, setForm] = useState<HouseSettings | null>(null);
  const [baseline, setBaseline] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (q.data && form == null) { setForm(q.data); setBaseline(JSON.stringify(q.data)); }
  }, [q.data, form]);

  if (q.loading && !form) return <div className={s.loading}>Loading…</div>;
  if (q.error && !form) return <Pending err={q.error} />;
  if (!form) return <div className={s.empty}>No defaults returned.</div>;
  const settings = form;

  const edit = (mut: (d: HouseSettings) => HouseSettings) => {
    setSaved(false);
    setForm((cur) => mut(structuredClone(cur ?? settings)));
  };
  const setPace = (key: string, patch: Partial<HousePace>) =>
    edit((d) => ({ ...d, paces: d.paces.map((p) => (p.key === key ? { ...p, ...patch } : p)) }));
  const setTpl = (key: string, patch: Partial<HouseScoringTemplate>) =>
    edit((d) => ({ ...d, scoring_templates: d.scoring_templates.map((t) => (t.key === key ? { ...t, ...patch } : t)) }));

  async function save() {
    if (saving) return;
    setSaving(true); setErr(null); setSaved(false);
    try {
      const next = await putHouseSettings(settings);
      setForm(next);
      setBaseline(JSON.stringify(next));
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save the defaults.');
    } finally {
      setSaving(false);
    }
  }

  const dirty = JSON.stringify(form) !== baseline;
  return (
    <div className={s.defaults}>
      <p className={s.lead}>These are the app-wide defaults every new league starts from. Changing them here doesn’t touch leagues that already exist — only what future ones inherit.</p>

      <section className={s.sec}>
        <h3>Pace options</h3>
        <p className={s.subtle}>Which paces new leagues can offer, and how long each phase stays open. New leagues inherit these; existing ones keep their own.</p>
        <div className={s.tablewrap}>
          <table className={s.table}>
            <thead>
              <tr><th>Pace</th><th className={s.num}>Submit (hrs)</th><th className={s.num}>Vote (hrs)</th><th>Offered</th></tr>
            </thead>
            <tbody>
              {settings.paces.map((p) => (
                <tr key={p.key}>
                  <td><b>{p.label}</b> <span className={s.dim}>{p.key}</span></td>
                  <td className={s.num}>{numField(p.submit_hours, (n) => setPace(p.key, { submit_hours: n }), 1)}</td>
                  <td className={s.num}>{numField(p.vote_hours, (n) => setPace(p.key, { vote_hours: n }), 1)}</td>
                  <td>
                    <label className={s.check}>
                      <input type="checkbox" checked={p.enabled} onChange={(e) => setPace(p.key, { enabled: e.target.checked })} />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={s.sec}>
        <h3>Default scoring</h3>
        <p className={s.subtle}>The starter scoring templates offered when a league is created.</p>
        <div className={s.tplgrid}>
          {settings.scoring_templates.map((t) => (
            <div key={t.key} className={s.tpl}>
              <div className={s.tplhead}>
                <b>{t.name}</b>
                <label className={s.check}>
                  <input type="checkbox" checked={t.veto_enabled} onChange={(e) => setTpl(t.key, { veto_enabled: e.target.checked })} />
                  <span>Veto</span>
                </label>
              </div>
              <div className={s.frows}>
                <label className={s.fr}><span>Budget</span>{numField(t.budget, (n) => setTpl(t.key, { budget: n }), 1)}</label>
                <label className={s.fr}><span>Min / track</span>{numField(t.track_min, (n) => setTpl(t.key, { track_min: n }))}</label>
                <label className={s.fr}><span>Max / track</span>{numField(t.track_max, (n) => setTpl(t.key, { track_max: n }))}</label>
                <label className={s.fr}><span>Down-vote ×</span>{numField(t.negative_multiplier, (n) => setTpl(t.key, { negative_multiplier: n }), 1)}</label>
                <label className={s.fr}><span>Veto at %</span>{numField(t.veto_threshold_pct, (n) => setTpl(t.key, { veto_threshold_pct: n }), 1, 100)}</label>
                <label className={s.fr}><span>Veto penalty</span>{numField(t.veto_penalty, (n) => setTpl(t.key, { veto_penalty: n }), 0)}</label>
                <label className={s.fr}><span>Popular bonus</span>{numField(t.popularity_bonus, (n) => setTpl(t.key, { popularity_bonus: n }), 0)}</label>
                <label className={s.fr}><span>Popular tie</span>{numField(t.popularity_tie_bonus, (n) => setTpl(t.key, { popularity_tie_bonus: n }), 0)}</label>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className={s.savebar}>
        {err ? <span className={s.err}>{err}</span> : saved ? <span className={s.ok}>Saved ✓</span> : null}
        <button className={s.save} disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save defaults'}</button>
      </div>
    </div>
  );
}

export function OwnerConsole() {
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  return (
    <div className="tp-root" data-mode={mode}>
      <div className={s.wrap}>
        <header className={s.top}>
          <button className={s.brand} onClick={() => navigate('/leagues')}>
            <span className={s.mk}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18V6l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></span>
            TPIAP
          </button>
          <div className={s.topr}>
            <ThemeSwitch />
            <button className={s.back} onClick={() => navigate('/leagues')}>← Your leagues</button>
          </div>
        </header>

        <div className={s.body}>
          <div className={s.head}>
            <span className={s.eyebrow}>Platform owner</span>
            <h1>App control</h1>
            <p>Everything across every league — and the global defaults new leagues inherit. Only you can see this.</p>
          </div>

          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.key} className={`${s.tab}${tab === t.key ? ' ' + s.tabon : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>

          <div className={s.panel}>
            {tab === 'overview' ? <Overview /> : tab === 'leagues' ? <Leagues /> : tab === 'members' ? <Members /> : <Defaults />}
          </div>
        </div>
      </div>
    </div>
  );
}
